import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import CreateActionButton from '@/components/patterns/create-action-button'
import OccasionPicker from '@/components/form/OccasionPicker'
import SaveBar from '@/components/forms/SaveBar'
import { HomepageBannerCompositionFields } from '@/components/forms/DesignForm/HeroCompositionFields'
import StorefrontPreviewFrame from '@/components/forms/DesignForm/StorefrontPreviewFrame'
import {
  normalizeHeroValuesForEditing,
  serializeHeroValues,
} from '@/components/forms/DesignForm/heroValues'
import {
  WebsiteEditorSplitLayout,
  WorkspaceSectionCard,
} from '@/components/forms/DesignForm/WebsiteDesignWorkspace'
import LazyPhotoManagerUploadDialog from '@/components/photo-manager/LazyPhotoManagerUploadDialog'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import SeasonalFocalPointDialog, {
  resolveSeasonalFocalPoint,
} from '@/components/seasonal/SeasonalFocalPointDialog'
import { buildSeasonalDraftPreviewDesign } from '@/components/seasonal/seasonalDesignPreview'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dropzone } from '@/components/ui/dropzone'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { useAppForm } from '@/lib/form'
import { PhotoManagerMode, PhotoManagerSelectionMode } from '@/lib/photo-manager-config'
import { useDefaultDesign } from '@/lib/services/designs/designs.query'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import {
  useArchiveSeasonalDesignMutation,
  useCreateSeasonalDesignMutation,
  usePublishSeasonalDesignMutation,
  useRollbackSeasonalDesignMutation,
  useSeasonalDesignImpactMutation,
  useSeasonalDesignRevisions,
  useUpdateSeasonalDesignMutation,
} from '@/lib/services/seasonal-content/seasonal-content.query'
import { seasonalDesignSchema } from '@/lib/services/seasonal-content/seasonal-content.schema'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { getStockImageUploadEndpoint } from '@/lib/services/stock-images/stock-images.api'
import {
  stockImagesQueryKeys,
  useCreateStockImageMutation,
  useStockImage,
  useStockImages,
  useStockImageTags,
} from '@/lib/services/stock-images/stock-images.query'
import { useStoreThemes } from '@/lib/services/stores/stores.query'
import { getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Archive, History, Image as ImageIcon, RotateCw, Settings2, Wallpaper } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

const createEmptyDesign = () => ({
  name: '',
  seasonal_tag_id: '',
  photo_asset_id: '',
  hero: {
    title: '',
    subtext: '',
    buttonText: 'Shop now',
    layout: 'professional',
    imageFocalPointX: null,
    imageFocalPointY: null,
    imageAltText: '',
    preferredTextPosition: 'left',
    preferredTextTone: 'auto',
  },
  sort_order: 0,
})

const designValues = (design) => {
  const emptyDesign = createEmptyDesign()
  if (!design) return emptyDesign

  const draftHero = design.draft_hero ?? {}
  const supportedHeroKeys = [...Object.keys(emptyDesign.hero), 'showText', 'showButton']
  const supportedHero = Object.fromEntries(
    supportedHeroKeys
      .filter((key) => Object.hasOwn(draftHero, key))
      .map((key) => [key, draftHero[key]])
  )

  return {
    name: design.name,
    seasonal_tag_id: design.seasonal_tag_id ?? design.occasion_id,
    photo_asset_id: design.draft_photo_asset_id,
    hero: normalizeHeroValuesForEditing(
      { ...emptyDesign.hero, ...supportedHero },
      {
        defaultButtonText: 'Shop now',
        defaultFocalPointX: null,
        defaultFocalPointY: null,
        defaultLayout: 'professional',
      }
    ),
    sort_order: design.sort_order,
  }
}

const photoName = (photo) =>
  photo?.title ||
  photo?.latest_media?.name ||
  photo?.original_media?.name ||
  `Stock image ${photo?.id}`

export function SeasonalDesignEditor({
  design = null,
  seasonalTags,
  canCreateStockImages,
  isReadOnly,
  organizationId,
}) {
  const path = usePathHelper()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const createMutation = useCreateSeasonalDesignMutation({ organizationId })
  const updateMutation = useUpdateSeasonalDesignMutation({ organizationId })
  const publishMutation = usePublishSeasonalDesignMutation({ organizationId })
  const impactMutation = useSeasonalDesignImpactMutation({ organizationId })
  const archiveMutation = useArchiveSeasonalDesignMutation({ organizationId })
  const rollbackMutation = useRollbackSeasonalDesignMutation({ organizationId })
  const createStockImageMutation = useCreateStockImageMutation()
  const [photoManagerOpen, setPhotoManagerOpen] = useState(false)
  const [focalPointOpen, setFocalPointOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [publishConfirmationOpen, setPublishConfirmationOpen] = useState(false)
  const [publishImpact, setPublishImpact] = useState(null)
  const form = useAppForm({
    defaultValues: designValues(design),
    onSubmitMeta: { intent: 'save' },
    validators: {
      onSubmit: seasonalDesignSchema,
    },
    onSubmit: async ({ value, meta, formApi }) => {
      try {
        const submittedValue = {
          ...value,
          hero: serializeHeroValues(value.hero),
        }

        if (meta?.intent === 'publish' && design) {
          const response = await publishMutation.mutateAsync({
            designId: design.id,
            values: submittedValue,
          })
          formApi.reset(value)
          setPublishImpact(null)
          setPublishConfirmationOpen(false)
          toast.success('Seasonal design published')
          if (response?.data?.id && Number(response.data.id) !== Number(design.id)) {
            await navigate({
              ...path.seasonalContent.show(response.data.id).using({ orgId: organizationId }).link,
              ignoreBlocker: true,
            })
          }
        } else if (design) {
          const response = await updateMutation.mutateAsync({
            designId: design.id,
            values: submittedValue,
          })
          formApi.reset(value)
          toast.success('Draft saved')
          if (response?.data?.id && Number(response.data.id) !== Number(design.id)) {
            await navigate({
              ...path.seasonalContent.show(response.data.id).using({ orgId: organizationId }).link,
              ignoreBlocker: true,
            })
          }
        } else {
          const response = await createMutation.mutateAsync({ values: submittedValue })
          const createdDesign = response?.data
          toast.success('Seasonal design created')
          if (createdDesign?.id) {
            await navigate({
              ...path.seasonalContent.show(createdDesign.id).using({ orgId: organizationId }).link,
              ignoreBlocker: true,
            })
          }
        }
      } catch (error) {
        toast.error(meta?.intent === 'publish' ? 'Could not publish' : 'Could not save draft', {
          description: getErrorMessage(error),
        })
        if (error?.response?.data?.errors) {
          formApi.setErrorMap({ onSubmit: { fields: error.response.data.errors } })
        }
      }
    },
  })
  const selectedSeasonalTagId = useStore(form.store, (state) =>
    Number(state.values.seasonal_tag_id)
  )
  const selectedSeasonalTag = seasonalTags.find((tag) => Number(tag.id) === selectedSeasonalTagId)
  const selectedSeasonIds =
    selectedSeasonalTag?.tag_type === 'season' ? [selectedSeasonalTagId] : []
  const selectedOccasionIds =
    selectedSeasonalTag?.tag_type === 'occasion' ? [selectedSeasonalTagId] : []
  const selectedPhotoId = useStore(form.store, (state) => Number(state.values.photo_asset_id))
  const compositionFocalPoint = useStore(form.store, (state) => ({
    x: state.values.hero.imageFocalPointX,
    y: state.values.hero.imageFocalPointY,
  }))
  const { data: stockTagsResponse } = useStockImageTags({ organizationId })
  const stockTags = stockTagsResponse?.data ?? stockTagsResponse ?? []
  const bannerTag = stockTags.find((tag) => tag.key === 'stock_usage_banner')
  const { data: imagesResponse } = useStockImages({
    filters: {
      ...(selectedOccasionIds.length ? { occasion_ids: selectedOccasionIds } : {}),
      ...(selectedSeasonIds.length ? { season_ids: selectedSeasonIds } : {}),
      tag_ids: bannerTag ? [Number(bannerTag.id)] : [],
    },
    pageSize: 80,
    enabled: selectedSeasonalTagId > 0 && Boolean(bannerTag),
  })
  const { data: selectedPhotoResponse } = useStockImage({
    stockImageId: selectedPhotoId,
    enabled: selectedPhotoId > 0,
  })
  const images = useMemo(
    () =>
      imagesResponse?.pages
        ?.flatMap((page) => page?.data ?? [])
        .filter((image) => image.is_published) ?? [],
    [imagesResponse?.pages]
  )
  const selectedPhoto =
    selectedPhotoResponse?.data ?? images.find((image) => Number(image.id) === selectedPhotoId)
  const selectedMedia = selectedPhoto?.latest_media ?? null
  const resolvedFocalPoint = resolveSeasonalFocalPoint(selectedMedia, compositionFocalPoint)
  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    publishMutation.isPending ||
    impactMutation.isPending
  const canReplaceBannerImage = canCreateStockImages && !isReadOnly
  const hasUnsavedChanges = useStore(form.store, (state) => !state.isDefaultValue)
  const createsNewIdentity =
    design &&
    Number(selectedSeasonalTagId) !== Number(design.seasonal_tag_id ?? design.occasion_id) &&
    (Boolean(design.published_revision_id) || Number(design.usage_count) > 0)
  const requestPublish = async () => {
    if (createsNewIdentity) {
      setPublishImpact({ createsNewIdentity: true })
      setPublishConfirmationOpen(true)
      return
    }

    try {
      const response = await impactMutation.mutateAsync({ designId: design.id })
      setPublishImpact(response?.data ?? response)
      setPublishConfirmationOpen(true)
    } catch (error) {
      toast.error('Could not calculate publishing impact', {
        description: getErrorMessage(error),
      })
    }
  }
  const publishDescription = publishImpact?.createsNewIdentity
    ? `Changing the occasion or season creates a new design. The ${design?.usage_count ?? 0} existing florist references stay on the current design and will not be updated.`
    : `${publishImpact?.usage_count ?? 0} florist websites reference this design; ${publishImpact?.live_impact_count ?? 0} are using it live now. Publishing updates those referenced websites immediately.`
  const resetCompositionFocalPoint = () => {
    form.setFieldValue('hero', {
      ...form.getFieldValue('hero'),
      imageFocalPointX: null,
      imageFocalPointY: null,
    })
  }

  const selectPhoto = (photoId) => {
    form.setFieldValue('photo_asset_id', photoId)
    resetCompositionFocalPoint()
  }

  const selectPhotoAssets = (assets) => {
    const selected = assets?.[0]
    if (selected?.id) selectPhoto(selected.id)
    return { insertedCount: selected ? 1 : 0, skippedCount: 0 }
  }

  const shuffleImage = () => {
    if (!images.length) return
    const index = images.findIndex((image) => Number(image.id) === selectedPhotoId)
    selectPhoto(images[(index + 1 + images.length) % images.length].id)
  }

  const createSeasonalStockImage = async (file) => {
    const response = await createStockImageMutation.mutateAsync({
      file,
      title: file.name.replace(/\.[^.]+$/, ''),
      description: '',
      is_published: true,
      seasonal_banner: true,
      tag_ids: [],
      occasion_ids: selectedOccasionIds,
      season_ids: selectedSeasonIds,
    })
    const stockImage = response?.data

    if (!stockImage?.id) throw new Error('The uploaded stock image did not return an id.')

    selectPhoto(stockImage.id)
    return stockImage.latest_media
  }

  const handleBannerUploadSuccess = (_file, response) => {
    const stockImage = response?.body?.data

    if (!stockImage?.id) return

    selectPhoto(stockImage.id)
    setPhotoManagerOpen(false)
    void queryClient.invalidateQueries({
      queryKey: stockImagesQueryKeys.all({ organizationId }),
    })
    toast.success('Banner added to Stock Images and selected')
  }

  const archiveDesign = async () => {
    try {
      await archiveMutation.mutateAsync({ designId: design.id })
      toast.success(design.archived_at ? 'Seasonal design restored' : 'Seasonal design archived')
    } catch (error) {
      toast.error('Could not update the design', { description: getErrorMessage(error) })
    }
  }

  const rollbackDesign = async (revisionId) => {
    try {
      await rollbackMutation.mutateAsync({ designId: design.id, revisionId })
      toast.success('Earlier revision restored and published')
    } catch (error) {
      toast.error('Could not restore the revision', { description: getErrorMessage(error) })
    }
  }

  return (
    <form.AppForm>
      <form.Form className="xl:h-full xl:min-h-0">
        <WebsiteEditorSplitLayout
          autoSaveId="global-seasonal-content-editor-v2"
          defaultSettingsSize={30}
          mobileLayout="natural"
          settingsPanel={
            <div className="h-full min-h-0 min-w-0 xl:overflow-hidden">
              <ScrollArea className="h-auto xl:h-full">
                <div className="space-y-3 pr-1 pb-3">
                  {design ? (
                    <DesignPublishingBar
                      design={design}
                      hasDraftChanges={hasUnsavedChanges || design.has_unpublished_changes}
                      isPending={isPending}
                      isReadOnly={isReadOnly}
                      onHistory={() => setHistoryOpen(true)}
                      onPublish={requestPublish}
                    />
                  ) : null}
                  <EditorSection
                    id="seasonal-design-details"
                    icon={Settings2}
                    title="Design details"
                    description="Name this reusable look and connect it to an occasion or season."
                    defaultOpen
                  >
                    <div className="grid gap-4">
                      <form.AppField name="name">
                        {(field) => <field.TextInput label="Design name" />}
                      </form.AppField>
                      <form.AppField name="seasonal_tag_id">
                        {() => (
                          <OccasionPicker
                            label="Occasion or season"
                            mode="single"
                            options={seasonalTags}
                            buttonLabel="Choose Occasion or Season"
                            dialogTitle="Choose Occasion or Season"
                            applyLabel="Apply Selection"
                            placeholder="Choose an occasion or season"
                            requireSelection
                          />
                        )}
                      </form.AppField>
                      {design?.published_revision_id ? (
                        <Typography variant="caption">
                          Changing a published design's occasion or season creates a new design
                          identity, so florist websites using the published version are not changed
                          unexpectedly.
                        </Typography>
                      ) : null}
                    </div>
                  </EditorSection>

                  <EditorSection
                    id="seasonal-banner-image"
                    icon={Wallpaper}
                    title="Homepage banner"
                    description="Choose the image, style, wording, and text treatment as one storefront look."
                  >
                    <div className="space-y-5">
                      <div className="space-y-3">
                        {selectedMedia?.url ? (
                          <div className="space-y-2">
                            <div className="overflow-hidden rounded-xl border bg-muted/20">
                              <div className="flex min-h-10 items-center gap-2 bg-zinc-950/85 px-3 text-white">
                                <span className="min-w-0 flex-1 truncate text-xs font-medium">
                                  {photoName(selectedPhoto)}
                                </span>
                                <Button
                                  type="button"
                                  size="compact"
                                  variant="onDark"
                                  className="shrink-0"
                                  disabled={!images.length || isReadOnly}
                                  onClick={shuffleImage}
                                >
                                  <RotateCw className="size-3.5" /> Shuffle
                                </Button>
                              </div>
                              <UploadedMediaPreviewTile
                                media={selectedMedia}
                                alt=""
                                wrapperClassName="overflow-hidden"
                                frameClassName="aspect-[16/7] w-full rounded-none"
                                imageClassName="h-full w-full object-cover"
                                imageStyle={{
                                  objectPosition: `${resolvedFocalPoint.x}% ${resolvedFocalPoint.y}%`,
                                }}
                                onEditComplete={canReplaceBannerImage ? () => {} : null}
                                saveEditedFile={
                                  canReplaceBannerImage
                                    ? ({ file }) => createSeasonalStockImage(file)
                                    : null
                                }
                                editUploadUsage={MediaUploadUsage.StorefrontHero.Value}
                                allowFocalPoint={!isReadOnly}
                                onAdjustFraming={isReadOnly ? null : () => setFocalPointOpen(true)}
                                onRemove={isReadOnly ? null : () => selectPhoto('')}
                                removeLabel="Remove banner image"
                              />
                            </div>
                            {!isReadOnly ? (
                              <CreateActionButton
                                context="inline"
                                variant="link"
                                className="h-auto p-0"
                                onClick={() => setPhotoManagerOpen(true)}
                              >
                                Change image
                              </CreateActionButton>
                            ) : null}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Dropzone asChild disabled={isReadOnly || !selectedSeasonalTagId}>
                              <button
                                type="button"
                                data-raw-button-owner="dropzone-trigger"
                                className="group grid aspect-[16/7] w-full place-items-center text-muted-foreground hover:text-foreground"
                                disabled={isReadOnly || !selectedSeasonalTagId}
                                onClick={() => setPhotoManagerOpen(true)}
                              >
                                <span className="grid justify-items-center gap-2 text-sm font-medium">
                                  <ImageIcon className="size-7" />
                                  {selectedSeasonalTagId
                                    ? 'Click to select a banner image'
                                    : 'Choose an occasion or season first'}
                                </span>
                              </button>
                            </Dropzone>
                            {selectedSeasonalTagId ? (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0"
                                disabled={isReadOnly || images.length === 0}
                                onClick={shuffleImage}
                              >
                                <RotateCw className="size-3.5" /> Shuffle
                              </Button>
                            ) : null}
                          </div>
                        )}
                        <Typography variant="supporting" className="block">
                          Use a 3840×2160 sRGB WebP around 1MB. Keep the subject in the middle 50%
                          with expendable space around it for desktop and mobile crops.
                        </Typography>
                        <SeasonalFocalPointControl
                          form={form}
                          media={selectedMedia}
                          open={focalPointOpen}
                          onOpenChange={setFocalPointOpen}
                        />
                        <LazyPhotoManagerUploadDialog
                          open={photoManagerOpen}
                          onOpenChange={setPhotoManagerOpen}
                          onUploadSuccess={handleBannerUploadSuccess}
                          onSelectAssets={selectPhotoAssets}
                          uploadUsage={MediaUploadUsage.StorefrontHero.Value}
                          uploadEndpoint={getStockImageUploadEndpoint()}
                          uploadMeta={(file) => ({
                            title: file.name.replace(/\.[^.]+$/, ''),
                            description: '',
                            is_published: '1',
                            seasonal_banner: '1',
                            tag_ids: '[]',
                            occasion_ids: JSON.stringify(selectedOccasionIds),
                            season_ids: JSON.stringify(selectedSeasonIds),
                          })}
                          photoManagerMode={
                            canCreateStockImages && !isReadOnly
                              ? PhotoManagerMode.UploadAndStock
                              : PhotoManagerMode.StockOnly
                          }
                          selectionMode={PhotoManagerSelectionMode.Single}
                          defaultTab="stock"
                          initialStockTagIds={bannerTag ? [Number(bannerTag.id)] : []}
                          initialStockOccasionIds={selectedOccasionIds}
                          initialStockSeasonIds={selectedSeasonIds}
                          stockOccasionOptions={seasonalTags}
                          copyStockAssets={false}
                        />
                      </div>
                      <HomepageBannerCompositionFields fieldRoot="hero" />
                      <Typography variant="supporting" className="block">
                        Image crop and contrast use storefront-safe defaults. Review the desktop and
                        mobile previews before publishing.
                      </Typography>
                    </div>
                  </EditorSection>
                </div>
              </ScrollArea>
            </div>
          }
          previewPanel={
            <SeasonalDraftPreview
              form={form}
              selectedPhoto={selectedPhoto}
              organizationId={organizationId}
            />
          }
        />
        {!isReadOnly ? (
          <SaveBar
            mutation={{ isPending }}
            submitButtonText={design ? 'Save draft' : 'Create draft'}
            unsavedChangesText={design ? 'Unsaved seasonal draft' : 'New seasonal design'}
          />
        ) : null}
        {design ? (
          <>
            <ConfirmationDialog
              open={publishConfirmationOpen}
              onOpenChange={setPublishConfirmationOpen}
              title={`Publish ${design.name}?`}
              description={publishDescription}
              onConfirm={() => form.handleSubmit({ intent: 'publish' })}
              confirmText={design.published_revision_id ? 'Publish changes' : 'Publish design'}
              isPending={publishMutation.isPending}
              trigger={null}
            />
            <SeasonalDesignVersionsDialog
              design={design}
              open={historyOpen}
              onOpenChange={setHistoryOpen}
              isReadOnly={isReadOnly}
              isArchiving={archiveMutation.isPending}
              isRollingBack={rollbackMutation.isPending}
              onArchive={archiveDesign}
              onRollback={rollbackDesign}
            />
          </>
        ) : null}
      </form.Form>
    </form.AppForm>
  )
}

function EditorSection({ id, icon, title, description, defaultOpen = false, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <WorkspaceSectionCard
      id={id}
      icon={icon}
      title={title}
      description={description}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      {children}
    </WorkspaceSectionCard>
  )
}

function SeasonalFocalPointControl({ form, media, open, onOpenChange }) {
  const imageFocalPointX = useStore(form.store, (state) => state.values.hero.imageFocalPointX)
  const imageFocalPointY = useStore(form.store, (state) => state.values.hero.imageFocalPointY)
  const value = resolveSeasonalFocalPoint(media, {
    x: imageFocalPointX,
    y: imageFocalPointY,
  })

  const apply = (position) => {
    form.setFieldValue('hero', {
      ...form.getFieldValue('hero'),
      imageFocalPointX: position.x,
      imageFocalPointY: position.y,
    })
  }

  return (
    <SeasonalFocalPointDialog
      open={open}
      onOpenChange={onOpenChange}
      media={media}
      value={value}
      onApply={apply}
    />
  )
}

function SeasonalDraftPreview({ form, selectedPhoto, organizationId }) {
  const { data: organization } = useOrganization(organizationId)
  const { data: defaultDesign } = useDefaultDesign({ organizationId })
  const { data: themes = [] } = useStoreThemes({ organizationId })
  const values = useStore(form.store, (state) => state.values)
  const [device, setDevice] = useState('desktop')
  const previewDesign = buildSeasonalDraftPreviewDesign({
    defaultDesign,
    heroValues: values.hero,
    selectedMedia: selectedPhoto?.latest_media,
  })

  return (
    <div className="h-full min-h-0 overflow-hidden">
      <StorefrontPreviewFrame
        organization={organization}
        design={previewDesign}
        defaultDesign={defaultDesign}
        themes={themes}
        previewPage="home"
        onPreviewPageChange={() => {}}
        previewDevice={device}
        onPreviewDeviceChange={setDevice}
        className="h-full min-h-0"
        viewportClassName="h-full min-h-0"
      />
    </div>
  )
}

function DesignPublishingBar({
  design,
  hasDraftChanges,
  isPending,
  isReadOnly,
  onHistory,
  onPublish,
}) {
  const status = design.archived_at
    ? 'Archived'
    : design.published_revision_id
      ? 'Published'
      : 'Draft'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
      <Badge variant={design.published_revision_id ? 'success' : 'outline'}>{status}</Badge>
      <Badge variant="muted" className="mr-auto">
        {hasDraftChanges ? 'Unpublished changes' : 'Up to date'}
      </Badge>
      <Button type="button" size="sm" variant="ghost" onClick={onHistory}>
        <History className="size-4" /> Versions
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={isReadOnly || isPending || !hasDraftChanges}
        onClick={onPublish}
      >
        {design.published_revision_id ? 'Publish changes' : 'Publish design'}
      </Button>
    </div>
  )
}

function SeasonalDesignVersionsDialog({
  design,
  open,
  onOpenChange,
  isReadOnly,
  isArchiving,
  isRollingBack,
  onArchive,
  onRollback,
}) {
  const [page, setPage] = useState(1)
  const { data: response, isFetching } = useSeasonalDesignRevisions({
    designId: design.id,
    page,
    enabled: open,
  })
  const payload = Array.isArray(response?.data) ? response : (response?.data ?? response)
  const revisions = payload?.data ?? []
  const meta = payload?.meta ?? {}

  const changeOpen = (nextOpen) => {
    if (!nextOpen) setPage(1)
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Published versions</DialogTitle>
          <DialogDescription>
            Each publish creates an immutable version. Restoring one publishes it again as the
            newest version.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55dvh] space-y-2 overflow-y-auto pr-1">
          {isFetching && revisions.length === 0 ? (
            <Typography variant="supporting" align="center" className="block py-6">
              Loading versions…
            </Typography>
          ) : null}
          {!isFetching && revisions.length === 0 ? (
            <Typography variant="supporting" align="center" className="block py-6">
              This design has not been published yet.
            </Typography>
          ) : null}
          {revisions.map((revision) => (
            <div
              key={revision.id}
              className="flex min-h-11 items-center justify-between gap-3 rounded-lg border px-3"
            >
              <span className="text-sm">
                Version {revision.revision} · {dayjs(revision.published_at).format('M/D/YYYY')}
              </span>
              {revision.id !== design.published_revision_id ? (
                <ConfirmationDialog
                  title={`Restore version ${revision.revision}?`}
                  description="This publishes a new version using the earlier image and wording."
                  onConfirm={() => onRollback(revision.id)}
                  confirmText="Restore and publish"
                  trigger={
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={isReadOnly || isRollingBack}
                    >
                      Restore
                    </Button>
                  }
                />
              ) : (
                <Badge variant="outline">Current</Badge>
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="items-center sm:justify-between">
          <ConfirmationDialog
            title={design.archived_at ? 'Restore this design?' : 'Archive this design?'}
            description={
              design.archived_at
                ? 'It will be available for new selections again.'
                : `Existing references will keep working. ${design.usage_count} florist websites currently use it.`
            }
            onConfirm={onArchive}
            confirmText={design.archived_at ? 'Restore' : 'Archive'}
            trigger={
              <Button type="button" size="sm" variant="ghost" disabled={isReadOnly || isArchiving}>
                <Archive className="size-4" /> {design.archived_at ? 'Restore' : 'Archive'}
              </Button>
            }
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={page >= (meta.last_page ?? 1) || isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
